/** System themes (see `src/themes/themes.css`). `arctic` is the default. */
export type ThemePalette = 'arctic' | 'navy'
export type ThemeMode = 'light' | 'dark'

export const THEME_PALETTES: readonly ThemePalette[] = ['arctic', 'navy']

/** Fallbacks if `--theme-chrome` can't be read (matches the nav bar colour per theme). */
const FALLBACK_LIGHT = '#e3eaf3'
const FALLBACK_DARK = '#18202b'

export function syncChromeThemeColor(theme?: ThemeMode) {
  if (!document.documentElement.classList.contains('app-ready')) return
  const root = document.documentElement
  const isDark = theme === 'dark' || (theme !== 'light' && root.classList.contains('dark'))
  const chrome = getComputedStyle(root).getPropertyValue('--theme-chrome').trim()
  setThemeColorMeta(chrome || (isDark ? FALLBACK_DARK : FALLBACK_LIGHT))
}

/** Set `<html data-theme>` + `.dark` (the inline script in index.html does the same before first paint). */
export function applyThemeToDocument(mode: ThemeMode, palette: ThemePalette) {
  const root = document.documentElement
  root.classList.toggle('dark', mode === 'dark')
  root.dataset.theme = palette
  syncChromeThemeColor(mode)
}

let themeAnimTimer: number | undefined

/**
 * Run a theme change with a short (~220ms) colour transition on the root surfaces only
 * (`html.theme-anim`, see themes.css): colours/borders/shadows — no layout properties, no
 * snapshots, input never blocked. Instant before the app is ready or with reduced motion.
 */
export function withThemeTransition(apply: () => void) {
  const root = document.documentElement
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (reduce || !root.classList.contains('app-ready')) {
    apply()
    return
  }
  root.classList.add('theme-anim')
  apply()
  window.clearTimeout(themeAnimTimer)
  themeAnimTimer = window.setTimeout(() => root.classList.remove('theme-anim'), 260)
}

function setThemeColorMeta(color: string) {
  const meta = document.querySelector('meta[name="theme-color"]')
  meta?.setAttribute('content', color)
}
